import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DeleteIcon } from '@chakra-ui/icons';
import './App.css';
import { usePeopleApi } from './usePeopleApi';
import { useAppAuth } from './auth/AppAuthProvider';
import SkillMultiSelect from './components/SkillMultiSelect';
import useSkillsData from './hooks/useSkillsData';
import {
  Box,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Card,
  CardBody,
  List,
  ListItem,
  Avatar,
  Text,
  Input,
  Button,
  Badge,
  Spinner,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Select,
  SimpleGrid,
  Switch,
  Tooltip,
  VStack,
  HStack,
  Link as ChakraLink
} from '@chakra-ui/react';
import AdminProfileActions from './components/AdminProfileActions';
import { ANALYTICS_EVENTS, trackEvent } from './analytics';

const PAGE_LIMIT = 50;
const SOURCE_FILTER_OPTIONS = [
  { label: 'Internal Staff', value: 'SSO' }
];

const PeopleList = () => {
  const { accessToken, isAdmin } = useAppAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [modifiedAfter, setModifiedAfter] = useState('');
  const [dateInputType, setDateInputType] = useState('text');
  const [pendingSkillLevels, setPendingSkillLevels] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({
    name: '',
    skillLevels: [],
    sources: [],
    modifiedAfter: null
  });
  const [currentOffset, setCurrentOffset] = useState(0);
  const [personToDelete, setPersonToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const cancelDeleteRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [hasSearched, setHasSearched] = useState(false);

  const { skills, isSkillsLoading, skillsError, fetchSkills } = useSkillsData(accessToken);
  const {
    people,
    loading,
    error,
    creatingProfile,
    profileLinks,
    exportLinks,
    exporting,
    total,
    fetchPeople,
    deletePerson,
    handleCreateProfile,
    handleExportProfile,
    actionErrors
  } = usePeopleApi(accessToken);

  const parseSkillLevelsParam = useCallback((params) => {
    const parsed = new Map();
    for (const [key, value] of params) {
      const match = /^skilllevels\[([0-9]+)\]$/.exec(key);
      if (!match) continue;
      const id = Number(match[1]);
      const level = value === '' ? null : Number(value);
      if (!Number.isSafeInteger(id) || id <= 0 || parsed.has(id)) continue;
      if (value !== '' && (!/^[0-9]+$/.test(value) || !Number.isInteger(level) || level < 0 || level > 10)) continue;
      parsed.set(id, level);
    }
    return Array.from(parsed, ([id, level]) => ({ id, level }));
  }, []);

  const parseSourcesParam = useCallback((values) => {
    const rawValues = Array.isArray(values) ? values : values ? [values] : [];
    const allowedSources = new Set(SOURCE_FILTER_OPTIONS.map((option) => option.value));

    return Array.from(
      new Set(
        rawValues
          .flatMap((value) => value.split(','))
          .map((value) => value.trim())
          .filter((value) => allowedSources.has(value))
      )
    );
  }, []);

  const normalizeToIsoDate = useCallback((value) => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return parsed.toISOString();
  }, []);

  const formatDateInputValue = useCallback(
    (value) => {
      const normalized = normalizeToIsoDate(value);
      return normalized ? normalized.slice(0, 10) : '';
    },
    [normalizeToIsoDate]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const query = params.get('name') || '';
    const parsedSkillLevels = parseSkillLevelsParam(params);
    const parsedSources = parseSourcesParam(params.getAll('source'));
    const modifiedAfterParam = params.get('modifiedafter') || '';
    const normalizedModifiedAfter = normalizeToIsoDate(modifiedAfterParam);

    setSearchQuery(query);
    setPendingSkillLevels(parsedSkillLevels);
    setSelectedSources(parsedSources);
    setModifiedAfter(formatDateInputValue(modifiedAfterParam));

    const filters = {
      name: query.trim(),
      skillLevels: parsedSkillLevels,
      sources: parsedSources,
      modifiedAfter: normalizedModifiedAfter
    };

    setAppliedFilters(filters);
    setCurrentOffset(0);

    if (filters.name || filters.skillLevels.length > 0 || filters.sources.length > 0 || filters.modifiedAfter) {
      setHasSearched(true);
      fetchPeople({ ...filters, limit: PAGE_LIMIT, offset: 0 });
    } else {
      setHasSearched(false);
    }
  }, [location.search, location.pathname, fetchPeople, parseSkillLevelsParam, parseSourcesParam, formatDateInputValue, normalizeToIsoDate]);

  useEffect(() => {
    if (pendingSkillLevels === null) {
      return;
    }

    if (pendingSkillLevels.length === 0) {
      setSelectedSkills([]);
      setPendingSkillLevels(null);
      return;
    }

    if (skills.length === 0) {
      return;
    }

    const unresolvedIds = [];
    const nextSelected = pendingSkillLevels
      .map(({ id, level }) => {
        const match = skills.find((skill) => skill.skillid === id);
        if (match) {
          return { value: match.skillid, label: match.skillname, minimumLevel: level };
        }
        unresolvedIds.push(id);
        return null;
      })
      .filter(Boolean);

    if (unresolvedIds.length > 0 && process.env.NODE_ENV !== 'production') {
      console.warn('[PeopleList] Dropping unknown skills from pending selection', {
        unresolvedIds,
        skillsLoaded: skills.length
      });
    }

    setSelectedSkills(nextSelected);
    setPendingSkillLevels(null);
  }, [skills, pendingSkillLevels]);

  useEffect(() => {
    setDateInputType(modifiedAfter ? 'date' : 'text');
  }, [modifiedAfter]);

  const handleSkillSelect = (skill) => {
    setSelectedSkills((prev) => {
      if (prev.some((existing) => existing.value === skill.value)) {
        return prev;
      }
      return [...prev, { ...skill, minimumLevel: null }];
    });
  };

  const handleSkillRemove = (skillId) => {
    setSelectedSkills((prev) => prev.filter((skill) => skill.value !== skillId));
  };

  const handleMinimumLevelChange = (skillId, value) => {
    setSelectedSkills((prev) => prev.map((skill) =>
      skill.value === skillId ? { ...skill, minimumLevel: value === '' ? null : Number(value) } : skill
    ));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmedQuery = searchQuery.trim();
    const normalizedModifiedAfter = normalizeToIsoDate(modifiedAfter);
    const hasFilters = Boolean(trimmedQuery || selectedSkills.length > 0 || selectedSources.length > 0 || normalizedModifiedAfter);

    if (!hasFilters) {
      setHasSearched(false);
      setAppliedFilters({ name: '', skillLevels: [], sources: [], modifiedAfter: null });
      setCurrentOffset(0);
      navigate('');
      return;
    }

    setHasSearched(true);
    setCurrentOffset(0);
    // Which kinds of filter were used, never their values.
    trackEvent(ANALYTICS_EVENTS.PEOPLE_SEARCHED, {
      filters: [
        trimmedQuery && 'name',
        selectedSkills.length > 0 && 'skills',
        selectedSources.length > 0 && 'source',
        normalizedModifiedAfter && 'modified_after'
      ]
        .filter(Boolean)
        .join(',')
    });

    const params = new URLSearchParams();
    if (trimmedQuery) {
      params.set('name', trimmedQuery);
    }
    selectedSkills.forEach((skill) => {
      params.set(`skilllevels[${skill.value}]`, skill.minimumLevel == null ? '' : String(skill.minimumLevel));
    });
    if (selectedSources.length > 0) {
      params.set('source', selectedSources.join(','));
    }
    if (normalizedModifiedAfter) {
      params.set('modifiedafter', normalizedModifiedAfter);
    }
    const nextSearch = params.toString();
    navigate(nextSearch ? `?${nextSearch}` : '');
  };

  const handlePageChange = (pageIndex) => {
    if (!hasSearched) {
      return;
    }
    const nextOffset = pageIndex * PAGE_LIMIT;
    setCurrentOffset(nextOffset);
    fetchPeople({
      ...appliedFilters,
      limit: PAGE_LIMIT,
      offset: nextOffset
    });
  };

  const handleDeletePerson = async () => {
    if (!personToDelete || isDeleting) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deletePerson(personToDelete.personid);
      setPersonToDelete(null);
      const nextOffset = total - 1 <= currentOffset ? Math.max(0, currentOffset - PAGE_LIMIT) : currentOffset;
      setCurrentOffset(nextOffset);
      await fetchPeople({ ...appliedFilters, limit: PAGE_LIMIT, offset: nextOffset });
    } catch (error) {
      setDeleteError(error?.message || 'Failed to delete person');
    } finally {
      setIsDeleting(false);
    }
  };

  const totalPages = useMemo(() => {
    if (!hasSearched || total <= PAGE_LIMIT) {
      return 0;
    }
    return Math.ceil(total / PAGE_LIMIT);
  }, [hasSearched, total]);

  const currentPageIndex = Math.floor(currentOffset / PAGE_LIMIT);

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <Card boxShadow="md" borderRadius="md">
          <CardBody>
            <form onSubmit={handleSearch}>
              <VStack spacing={0} align="stretch" maxW="820px">
                <SimpleGrid columns={{ base: 1, md: 2 }} templateColumns={{ md: '150px minmax(0, 1fr)' }}
                  gap={{ base: 2, md: 5 }} alignItems="center" py={4} borderBottomWidth="1px" borderColor="gray.200">
                  <FormLabel htmlFor="people-name-filter" mb={0} fontWeight="medium">Name</FormLabel>
                  <Input id="people-name-filter" placeholder="Search by name…" value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)} maxW="420px" />
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2 }} templateColumns={{ md: '150px minmax(0, 1fr)' }}
                  gap={{ base: 2, md: 5 }} alignItems="center" py={4} borderBottomWidth="1px" borderColor="gray.200">
                  <FormLabel htmlFor="people-modified-filter" mb={0} fontWeight="medium">Modified after</FormLabel>
                  <Input id="people-modified-filter" type={dateInputType} placeholder="Select a date"
                    value={modifiedAfter} onChange={(e) => setModifiedAfter(e.target.value)} maxW="240px"
                    onFocus={() => setDateInputType('date')}
                    onBlur={(e) => { if (!e.target.value) setDateInputType('text'); }} />
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2 }} templateColumns={{ md: '150px minmax(0, 1fr)' }}
                  gap={{ base: 2, md: 5 }} alignItems="center" py={4} borderBottomWidth="1px" borderColor="gray.200">
                  <Text fontWeight="medium">Internal staff</Text>
                  <Flex align="center" gap={3}>
                    <Switch id="people-internal-filter" colorScheme="blue"
                      isChecked={selectedSources.includes('SSO')}
                      onChange={(event) => setSelectedSources(event.target.checked ? ['SSO'] : [])}
                      aria-label="Show internal staff only" />
                    <FormLabel htmlFor="people-internal-filter" mb={0} cursor="pointer">
                      Show internal staff only
                    </FormLabel>
                  </Flex>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2 }} templateColumns={{ md: '150px minmax(0, 1fr)' }}
                  gap={{ base: 2, md: 5 }} alignItems="start" py={4} borderBottomWidth="1px" borderColor="gray.200">
                  <Text fontWeight="medium" pt={{ md: 2 }}>Skills</Text>
                  <VStack align="stretch" spacing={4} minW={0}>
                    <Box maxW="420px" w="100%">
                      <SkillMultiSelect skills={skills} selectedSkills={selectedSkills}
                        onSelect={handleSkillSelect} onRemove={handleSkillRemove}
                        isLoading={isSkillsLoading} error={skillsError} onRetry={fetchSkills}
                        showSelectedTags={false} placeholder="Search and add a skill…" />
                    </Box>
                    {selectedSkills.length > 0 && (
                      <Box maxW="520px" w="100%">
                        <Grid templateColumns="minmax(0, 1fr) 160px 40px" gap={3} px={3} pb={2}
                          display={{ base: 'none', md: 'grid' }}>
                          <Text fontSize="sm" color="gray.600">Skill</Text>
                          <Text fontSize="sm" color="gray.600">Minimum level</Text>
                        </Grid>
                        <VStack align="stretch" spacing={0} borderWidth="1px" borderColor="gray.200" borderRadius="md">
                          {selectedSkills.map((skill, index) => (
                            <Grid key={skill.value}
                              templateColumns={{ base: 'minmax(0, 1fr) 40px', md: 'minmax(0, 1fr) 160px 40px' }}
                              gap={3} alignItems="center" px={3} py={3}
                              borderBottomWidth={index < selectedSkills.length - 1 ? '1px' : 0}
                              borderColor="gray.200">
                              <Text gridColumn={{ base: '1 / -1', md: '1' }} display="flex" alignItems="center"
                                minH={8} fontWeight="medium" minW={0} position="relative"
                                top={{ base: 0, md: '3px' }}>
                                {skill.label}
                              </Text>
                              <FormControl gridColumn={{ base: '1', md: '2' }}>
                                <FormLabel htmlFor={`minimum-level-${skill.value}`} fontSize="sm" mb={1}
                                  display={{ base: 'block', md: 'none' }}>Minimum level</FormLabel>
                                <Select id={`minimum-level-${skill.value}`} size="sm"
                                  aria-label={`Minimum level for ${skill.label}`}
                                  value={skill.minimumLevel == null ? '' : String(skill.minimumLevel)}
                                  onChange={(event) => handleMinimumLevelChange(skill.value, event.target.value)}>
                                  <option value="">Any level</option>
                                  {skill.minimumLevel != null && (skill.minimumLevel < 1 || skill.minimumLevel > 5) && (
                                    <option value={skill.minimumLevel}>{skill.minimumLevel} (from link)</option>
                                  )}
                                  {[1, 2, 3, 4, 5].map((level) => (
                                    <option key={level} value={level}>{level}</option>
                                  ))}
                                </Select>
                              </FormControl>
                              <Tooltip label={`Remove ${skill.label} filter`} hasArrow>
                                <IconButton gridColumn={{ base: '2', md: '3' }}
                                  alignSelf={{ base: 'end', md: 'center' }} size="sm"
                                  aria-label={`Remove ${skill.label} filter`} icon={<DeleteIcon />}
                                  variant="ghost" color="gray.500"
                                  _hover={{ color: 'red.600', bg: 'red.50' }}
                                  _focusVisible={{ color: 'red.600', boxShadow: 'outline' }}
                                  onClick={() => handleSkillRemove(skill.value)} />
                              </Tooltip>
                            </Grid>
                          ))}
                        </VStack>
                      </Box>
                    )}
                  </VStack>
                </SimpleGrid>

                <Flex justify={{ base: 'stretch', sm: 'flex-end' }} pt={4}>
                  <Button type="submit" size="sm" colorScheme="blue" minW="130px"
                    w={{ base: '100%', sm: 'auto' }}>Search people</Button>
                </Flex>
              </VStack>
            </form>
          </CardBody>
        </Card>

        <Card boxShadow="md" borderRadius="md">
          <CardBody>
            {loading && (
              <Flex justify="center" mt={4}>
                <Spinner />
              </Flex>
            )}
            {hasSearched && error && (
              <Text textAlign="center" mt={4} color="red.500">
                Error: {error}
              </Text>
            )}

            {!loading && !error && hasSearched && people.length === 0 && (
              <Text textAlign="center" mt={4} color="gray.500">
                No people found for the selected filters.
              </Text>
            )}

            {!loading && !error && people.length > 0 && (
              <>
                <List spacing={3}>
                  {people.map((person) => (
                    <ListItem
                      key={person.personid}
                      borderWidth="1px"
                      borderRadius="md"
                      p={4}
                      minH="40px"
                      position="relative"
                    >
                      <Flex justify="space-between" align="center" h="100%" gap={4} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
                        <Flex align="center" h="100%">
                          <Avatar name={person.name} size="sm" mr={4} bg="blue.200" color="white" />
                          <Text
                            color="blue.600"
                            fontSize="1.5rem"
                            lineHeight="1"
                            mb="0"
                          >
                            {person.name}
                          </Text>
                          {person.source === 'SSO' && (
                            <Badge colorScheme="blue" ml={3}>
                              Internal
                            </Badge>
                          )}
                        </Flex>
                        <HStack spacing={3} align="center" flexWrap="wrap" justify="flex-end">
                          {person.description != null ? (
                            <>
                              <ChakraLink
                                href={profileLinks[person.personid] ? profileLinks[person.personid] : `/HoggedIn?personid=${person.personid}`}
                                _hover={{ textDecoration: 'none' }}
                              >
                                <Button size="sm" colorScheme="blue" variant="outline">
                                  View Profile
                                </Button>
                              </ChakraLink>
                              {isAdmin && (
                                <AdminProfileActions
                                  person={person}
                                  exportLinks={exportLinks}
                                  onExportProfile={handleExportProfile}
                                  exporting={exporting}
                                  actionErrors={actionErrors}
                                  isVisible={isAdmin}
                                />
                              )}
                            </>
                          ) : (
                            <Button
                              size="sm"
                              colorScheme="blue"
                              variant="outline"
                              onClick={() => {
                                if (person.tt_id) {
                                  handleCreateProfile(person.personid, person);
                                  return;
                                }
                                navigate(`/HoggedIn?personid=${person.personid}&edit=1`);
                              }}
                              isDisabled={!!creatingProfile[person.personid]}
                              minW="110px"
                              display="inline-flex"
                              alignItems="center"
                              justifyContent="center"
                              _hover={{ bg: 'blue.50' }}
                              borderColor="blue.500"
                              color="blue.500"
                              _disabled={{ opacity: 1, borderColor: 'blue.500', color: 'blue.500', bg: 'transparent' }}
                            >
                              {creatingProfile[person.personid] ? (
                                <Spinner size="xs" color="blue.500" thickness="2px" speed="0.6s" />
                              ) : (
                                'Create Profile'
                              )}
                            </Button>
                          )}
                          {actionErrors?.[person.personid]?.message && (
                            <Text fontSize="sm" color="red.500">
                              {actionErrors[person.personid].message}
                            </Text>
                          )}
                          {isAdmin && (
                            <Button size="sm" colorScheme="red" variant="outline"
                              onClick={() => { setDeleteError(null); setPersonToDelete(person); }}
                              aria-label={`Delete ${person.name}`}>
                              Delete
                            </Button>
                          )}
                        </HStack>
                      </Flex>
                    </ListItem>
                  ))}
                </List>

                {totalPages > 0 && (
                  <Flex justify="center" mt={6}>
                    <HStack spacing={2} flexWrap="wrap" justify="center">
                      {Array.from({ length: totalPages }).map((_, index) => (
                        <Button
                          key={`page-${index}`}
                          size="sm"
                          colorScheme="blue"
                          variant={index === currentPageIndex ? 'solid' : 'outline'}
                          onClick={() => handlePageChange(index)}
                          minW="40px"
                        >
                          {index + 1}
                        </Button>
                      ))}
                    </HStack>
                  </Flex>
                )}
              </>
            )}
          </CardBody>
        </Card>
      </VStack>
      <AlertDialog isOpen={Boolean(personToDelete)} leastDestructiveRef={cancelDeleteRef}
        onClose={() => { if (!isDeleting) setPersonToDelete(null); }}
        closeOnOverlayClick={!isDeleting} closeOnEsc={!isDeleting}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete person</AlertDialogHeader>
            <AlertDialogBody>
              Permanently delete {personToDelete?.name} and their skill history? Shared skills will remain.
              This cannot be undone.
              {deleteError && <Text role="alert" color="red.500" mt={3}>{deleteError}</Text>}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelDeleteRef} onClick={() => setPersonToDelete(null)} isDisabled={isDeleting}>
                Cancel
              </Button>
              <Button colorScheme="red" ml={3} onClick={handleDeletePerson} isLoading={isDeleting}
                isDisabled={isDeleting}>
                Delete person
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default PeopleList;

